import { createModel } from '@rematch/core';
import { replace } from 'connected-react-router';

import { RootModel } from '../index';
import { get, put, post } from '../../api/api';
import { apiUrls, replaceUrls } from '../../api/apiUrls';
import AccountState, { BetHistoryData, BetHistoryPayload } from '../../interfaces/AccountState';
import { checkError } from 'src/utils/utils';

export default createModel<RootModel>()({
  state: {
    accountStatement: [],
    restrictionReasons: {},
    accountStatementFilter: {
      date1: '',
      date2: ''
    },
    limits: {
      depositLimitDay: 0.00,
      depositLimitMonth: 0.00,
      bookmakerDepositLimitDay: 0.00,
      bookmakerDepositLimitMonth: 0.00,
      totalDepositedDaily: 0.00,
      totalDepositedMonthly: 0.00,
      stakeLimitDay: 0.00,
      stakeLimitMonth: 0.00,
      timeframe: 'none',
      isBlocked: false,
      reasonCode: 1
    },
    transactionTypeList: [],
    customerTransactionTypeList: [],
    statistics: [],
    success: true,
    myBetsCount: 0
  } as AccountState,

  reducers: {
    setBetsCount(state, myBetsCount: AccountState['myBetsCount']) {
      return { ...state, myBetsCount };
    },

    setAccountStatment(state, accountStatement: AccountState['accountStatement']) {
      return { ...state, accountStatement };
    },

    setRestrictionReasons(state, payload: AccountState['restrictionReasons']) {
      return { ...state, restrictionReasons: { ...state.restrictionReasons, ...payload } };
    },

    setLimits(state, payload: AccountState['limits']) {
      return { ...state, limits: { ...state.limits, ...payload } };
    },

    setTransactionTypes(state, payload: AccountState['transactionTypeList']) {

      return { ...state, transactionTypeList: payload };
    },

    setCustomerTransactionTypes(state, payload: AccountState['transactionTypeList']) {

      return { ...state, customerTransactionTypeList: payload };
    },

    setStatistics(state, payload: AccountState['statistics']) {
      return { ...state, statistics: payload };
    },

    setFilterDate(state, payload: AccountState['accountStatementFilter']) {
      return { ...state, accountStatementFilter: { date1: payload.date1, date2: payload.date2 } };
    },

    setSuccess(state, payload: AccountState['success']) {
      return { ...state, success: payload };
    }
  },

  effects: (dispatch) => ({

    async getUserLimits(payload, state): Promise<void> {
      try {
        const response = await get(replaceUrls(apiUrls.userLimits, { id: state.auth.customerId }));

        if (response && response.status === 200) {
          dispatch.account.setLimits(response.data.results);
        } else {
          const message = checkError(response.data.results);
          dispatch.toast.showToast({ type: 'error', message: message || 'Oops! Something went wrong.' });
        }
      } catch (error) {
        // TODO: Handle error
      }
    },

    async getBetHistory({ page, fromDate, toDate, type, sort, product, transactionWallet, betType, limit }: BetHistoryPayload, state):
     Promise<BetHistoryData> {
      try {
        if (state.auth.customer?.accountNumber) {
          dispatch.events.setLoading(true);
          let bet = betType === 'allTypes' || !betType ? 0 : 1;

          if (betType === 'system'){
            bet = 3;
          } else if (betType === 'acca'){
            bet = 2;
          }

          const response = await get(
            `${ replaceUrls(apiUrls.betsHistory, {
              accountNumber: state.auth?.customer?.accountNumber
            })
            }?page=${ page }&fromDate=${ fromDate }&toDate=${
              toDate }&type=${ type }&sort=${ sort }&product=${
              product }&transactionWallet=${ transactionWallet }&betType=${ bet }&limit=${ limit }`
          );

          const data: BetHistoryData = { results: { collection: [] }, lastPage: false };
          if (response && response.status === 200) {
            data.results = response.data.results;

            if (response.data.results.collection.length < 10 && page !== -1) {
              data.lastPage = true;
            }
          }

          dispatch.events.setLoading(false);

          return data;
        } else {
          return { results: { collection: [] }, lastPage: false };
        }
      } catch (error) {
        // TODO: Handle error
        return { results: { collection: [] }, lastPage: false };
      }
    },

    async getBetHistoryCount(payload, state): Promise<void> {
      try {
        if (state.auth.customer?.accountNumber) {
          dispatch.events.setLoading(true);
          const response = await get(replaceUrls(apiUrls.betsHistoryCount, { accountNumber: state.auth?.customer?.accountNumber }));
          dispatch.account.setBetsCount(response.data?.results.status.open.tickets);
          dispatch.events.setLoading(false);

          return response;
        }
      } catch (error) {
        // TODO: Handle error
      }
    },

    async getRestrictionReasons(): Promise<void> {
      try {
        const response = await get(replaceUrls(apiUrls.restrictionReasons));

        if (response && response.status === 200) {
          dispatch.account.setRestrictionReasons(response.data.results);
        } else {
          const message = checkError(response.data.results);
          dispatch.toast.showToast({ type: 'error', message: message || 'Oops! Something went wrong.' });
        }
      } catch (error) {
        // TODO: Handle error
      }
    },

    async getUserRestrictions(payload, state): Promise<void> {
      try {
        const response = await get(replaceUrls(apiUrls.userRestrictions, { id: state.auth.customerId }));

        if (response && response.status === 200) {
          dispatch.account.setLimits(response.data.results);
        } else {
          const message = checkError(response.data.results);
          dispatch.toast.showToast({ type: 'error', message: message || 'Oops! Something went wrong.' });
        }
      } catch (error) {
        // TODO: Handle error
      }
    },

    async updateUserLimits(payload, state): Promise<void> {
      try {
        const {
          depositLimitDay,
          depositLimitMonth,
          stakeLimitDay,
          stakeLimitMonth
        } = payload;

        if ((depositLimitMonth > 0 && depositLimitDay > depositLimitMonth) || (stakeLimitMonth > 0 && stakeLimitDay > stakeLimitMonth)) {
          dispatch.toast.showToast({ type: 'error', message: 'Daily limit can\'t be higher than monthly limit.' });
          return;
        }

        const response = await put(replaceUrls(apiUrls.userLimits, { id: state.auth.customerId }), payload);

        if (response && response.status === 200) {
          dispatch.account.getUserLimits();
          dispatch.toast.showToast({ type: 'success', message: (response && response.data.message) });
        } else if (response && response.status === 403) {
          const message = checkError(response.data.results);
          dispatch.toast.showToast({ type: 'error', message: message || 'Oops! Something went wrong.' });
        } else {
          const message = checkError(response.data.results);
          dispatch.toast.showToast({ type: 'error', message: message || 'Oops! Something went wrong.' });
        }
      } catch (error) {
        // TODO: Handle error
        dispatch.toast.showToast({ type: 'error', message: 'Oops! Something went wrong.' });
      }
    },

    async getAccountStatement(payload, state): Promise<void> {
      try {
        const customer = state.auth.customer;
        const query = Object.keys(payload).map((key) => {
          return encodeURIComponent(key) + '=' + encodeURIComponent(payload[key]);
        }).join('&');
        const response = await get(`${ apiUrls.userTransactions.replace(':id', customer.id) }?${ query }&orderBy=createdAt`);

        if (response && response.status === 200) {
          dispatch.account.setAccountStatment(response.data.results.transactions);
          dispatch.account.setStatistics(response.data.results.statistics);
        } else {
          const message = checkError(response.data.results);
          dispatch.toast.showToast({ type: 'error', message: message || 'Oops! Something went wrong.' });
        }

      } catch (error) {
        dispatch.toast.showToast({ type: 'error', message: 'Oops! Something went wrong.' });
      }
    },

    async updateUserRestrictions(payload, state): Promise<void> {
      try {
        const response = await put(replaceUrls(apiUrls.userRestrictions, { id: state.auth.customerId }), payload);

        if (response && response.status === 200) {
          await dispatch.account.getUserRestrictions();
          dispatch.toast.showToast({ type: 'success', message: (response && response.data.message) });
        } else if (response && response.status === 403) {
          const message = checkError(response.data.results);
          dispatch.toast.showToast({ type: 'error', message: message || 'Oops! Something went wrong.' });

        } else {
          const message = checkError(response.data.results);
          dispatch.toast.showToast({ type: 'error', message: message || 'Oops! Something went wrong.' });
        }
      } catch (error) {
        // TODO: Handle error
        dispatch.toast.showToast({ type: 'error', message: 'Oops! Something went wrong.' });
      }
    },

    async saveCustomer(payload, state): Promise<void> {
      try {
        const customer = state.auth.customer;

        const info = {
          'birthdate': payload.birthdate,
          'address': {
            'addressLine1': payload.address,
            'addressLine2': '',
            'city': payload.city,
            'postCode': payload.postalCode,
            'country': payload.country
          },
          'subscribedToNewsletter': payload.receiveNewsLetter,
          'subscribedToNewProducts': payload.promotions,
          'personalIdNumber': payload.idNumber,
          'currentPassword': payload.currentPassword,
          'plainPassword': {
            'first': payload.newPassword,
            'second': payload.confirmPassword
          }
        };

        const response = await put(`${ apiUrls.users }/${ customer.id }`, info);

        if (response && response.status === 200){
          dispatch.auth.getCustomer();
          dispatch.toast.showToast({ type: 'success', message: (response && response.data.message) });
        } else if (response && response.status === 403) {
          const message = checkError(response.data.results);
          dispatch.toast.showToast({ type: 'error', message: message || 'Oops! Something went wrong.' });
          dispatch.account.setSuccess(false);
        } else {
          const message = checkError(response.data.results);
          dispatch.toast.showToast({ type: 'error', message: message || 'Oops! Something went wrong.' });
        }

      } catch (error) {
        dispatch.toast.showToast({ type: 'error', message: 'Oops! Something went wrong.' });
      }
    },

    async requestWithdrawal(payload, state): Promise<void> {

      try {
        const response = await post(apiUrls.requestWithdrawal, {
          customer: state.auth.customerId,
          requestedAmount: (payload.amount * 100),
          divisor: 100,
          method: 'online_account',
          ...payload
        });

        if (response && response.status === 200) {
          const date = Date.now();
          dispatch.events.setLoading(false);
          dispatch(replace(`#withdraw-modal?&amount=${ payload.amount }&date=${ date }`));
          dispatch.toast.showToast({ type: 'success', message: (response && response.data.message) });
          dispatch.auth.getCustomer();
        } else if (response && response.status === 403) {
          const message = checkError(response.data.results);
          dispatch.toast.showToast({ type: 'error', message: message || 'Oops! Something went wrong.' });
        } else {
          const message = checkError(response.data.results);
          dispatch.toast.showToast({ type: 'error', message: message || 'Oops! Something went wrong.' });
        }
      } catch (error) {
        dispatch.toast.showToast({ type: 'error', message: 'Oops! Something went wrong.' });
      }
    },

    async checkValidity(payload, state): Promise<boolean> {
      await dispatch.account.getUserLimits();
      await dispatch.account.getUserRestrictions();

      const {
        timeframe,
        isBlocked,
        depositLimitDay,
        depositLimitMonth,
        bookmakerDepositLimitDay,
        bookmakerDepositLimitMonth,
        totalDepositedDaily,
        totalDepositedMonthly
      } = state.account.limits;
      const amount = Number(payload);
      let valid = true;

      if (timeframe !== 'none') {
        valid = false;
        dispatch.toast.showToast({ type: 'error', message: 'Account within cooling-off period' });
      } else if (isBlocked) {
        valid = false;
        dispatch.toast.showToast({ type: 'error', message: 'Account disabled' });
      } else if ((totalDepositedDaily >= depositLimitDay) || (bookmakerDepositLimitDay > 0 && totalDepositedDaily >= bookmakerDepositLimitDay)) {
        valid = false;
        dispatch.toast.showToast({ type: 'error', message: 'Daily limit reached' });
      } else if (
        (totalDepositedMonthly >= depositLimitMonth) ||
        (bookmakerDepositLimitMonth > 0 && totalDepositedDaily >= bookmakerDepositLimitMonth)
      ) {
        valid = false;
        dispatch.toast.showToast({ type: 'error', message: 'Monthly limit reached' });
      } else if ((totalDepositedDaily + amount) > depositLimitDay) {
        valid = false;
        dispatch.toast.showToast({ type: 'error', message: 'The amount exceeds the daily limit set' });
      } else if ((totalDepositedMonthly + amount) > depositLimitMonth) {
        valid = false;
        dispatch.toast.showToast({ type: 'error', message: 'The amount exceeds the monthly limit set' });
      } else if (bookmakerDepositLimitDay > 0 && (totalDepositedDaily + amount) > bookmakerDepositLimitDay) {
        valid = false;
        dispatch.toast.showToast({ type: 'error', message: 'The amount exceeds the daily limit set by the bookmaker' });
      } else if (bookmakerDepositLimitMonth > 0 && (totalDepositedMonthly + amount) > bookmakerDepositLimitMonth) {
        valid = false;
        dispatch.toast.showToast({ type: 'error', message: 'The amount exceeds the monthly limit set by the bookmaker' });
      }

      return valid;
    },

    updateFilterDate(payload): void {
      dispatch.account.setFilterDate(payload);
    },

    async getTransactionTypes(): Promise<void>{
      try {
        const response = await get(replaceUrls(apiUrls.getTransactionTypes));
        if (response && response.status === 200){
          dispatch.account.setTransactionTypes(response.data.results.collection);
        } else {
          const message = checkError(response.data.results);
          dispatch.toast.showToast({ type: 'error', message: message || 'Oops! Something went wrong.' });
        }
      } catch (e){
        // dispatch.toast.showToast({ type: 'error', message: 'Oops! Something went wrong.' });
      }
    },

    async getTransactionTypeList({ transactionWallet, createdAtFrom }, state): Promise<void>{
      try {
        const response = await get(`${ replaceUrls(apiUrls.getTransactionTypeList) }?transactionWallet=${
          transactionWallet }&createdAtFrom=${ createdAtFrom }&customer=${ state.auth.customerId }`);
        if (response && response.status === 200){
          dispatch.account.setCustomerTransactionTypes(response.data.results);
        } else {
          const message = checkError(response.data.results);
          dispatch.toast.showToast({ type: 'error', message: message || 'Oops! Something went wrong.' });
        }
      } catch (e){
        dispatch.toast.showToast({ type: 'error', message: 'Oops! Something went wrong.' });
      }
    },

    async submitOzow(payload): Promise<string>{
      try {
        const response = await post(apiUrls.ozowDeposit, payload);
        if (response && response.status === 200 && response.data.results && response.data.results.url){
          return response.data.results.url;
        }
        const message = checkError(response.data.results);
        dispatch.toast.showToast({ type: 'error', message: message || 'Oops! Something went wrong.' });
        return '';
      } catch (e){
        dispatch.toast.showToast({ type: 'error', message: 'Oops! Something went wrong.' });
        return '';
      }
    }
  })
});
